import { AuthRepository } from '~/repository/AuthRepository';
import type { QrLoginResponse } from '~/types/api/auth/qrLogin';
import { UnAuthorizedError } from '~/error/UnAuthorizedError';
import type { LoginResponse } from '~/types/api/auth/login';
import { ProxyRepository } from '~/repository/ProxyRepository';
import { ClientError } from '~/error/ClientError';
import { MessageCodeEnum } from '~/constants/MessageCode';
import { useSystemConfig } from '~/composables/common/useSystemConfig';

export function useAuth(displayId: string) {
  const authRepository = new AuthRepository(displayId);
  const loginUserStore = useLoginUserStore();
  const { startLoad, endLoad } = useLoadingStore();
  const router = useRouter();
  const { notifyLogin } = useMultiTabLoginControlLogic();
  const logger = useLogger(displayId);
  const { getStationWithOrganizationByStationCd } = useStation(displayId);
  const { getSpeedMonitoringSettings } = useSystemConfig(displayId);

  // QRコードによる認証
  const loginWithQrCode = async (qrCode: string) => {
    startLoad();
    let responseData: QrLoginResponse;
    try {
      // 認証エンドポイントを呼び出す
      const response = await authRepository.qrLogin(qrCode);

      responseData = response.data;

      // 他のタブにログインしたことを通知する
      notifyLogin();

      await _establishSession();
    }
    finally {
      endLoad();
    }

    // ストアにログインユーザー情報を保存する
    loginUserStore.setUserInfo(
      responseData.platId,
      responseData.accountType,
      responseData.userNameSei,
      responseData.userNameMei,
      responseData.stationAreaDetectionFlag,
      responseData.permissions,
      responseData.affiliations,
    );
  };

  /**
   * ID・パスワードによる認証
   *
   * @param platId PLAT ID
   * @param password パスワード
   */
  const loginWithIdPassword = async (platId: string, password: string) => {
    startLoad();
    let responseData: LoginResponse;
    try {
      // 認証エンドポイントを呼び出す
      const response = await authRepository.login({
        platId,
        password,
      });
      responseData = response.data;

      // 他のタブにログインしたことを通知する
      notifyLogin();

      await _establishSession();
    }
    finally {
      endLoad();
    }

    // ストアにログインユーザー情報を保存する
    loginUserStore.setUserInfo(
      responseData.platId,
      responseData.accountType,
      responseData.userNameSei,
      responseData.userNameMei,
      responseData.stationAreaDetectionFlag,
      responseData.permissions,
      responseData.affiliations,
      responseData.mustChangePassword,
    );
  };

  // ログアウト
  const logout = async () => {
    startLoad();
    try {
      await authRepository.logout();
    }
    finally {
      // 各Storeの内容を初期化する
      resetStore();
      endLoad();
      router.push('/login');
    }
  };

  // 認証状態の確認
  const checkAuth = async () => {
    // ストアにログインユーザー情報が設定されていない場合は未認証とする
    if (!loginUserStore.isLoggedIn()) {
      return false;
    }

    // セッション情報取得APIを呼び出し、正常終了したら認証OK
    try {
      await refreshSessionInfo();
    }
    catch (error) {
      // 401エラーが返ってきた場合は認証NG
      if (error instanceof UnAuthorizedError) {
        return false;
      }
      // 401エラー以外の場合はイレギュラーなのでエラーとする
      throw error;
    }

    return true;
  };

  /**
   * セッション情報を洗い替える
   */
  const refreshSessionInfo = async () => {
    startLoad();
    try {
      const response = await authRepository.getSessionInfo();
      const sessionInfo = response.data;

      // 取得した情報でストアの内容を更新する
      if (sessionInfo) {
        loginUserStore.updateUserInfo(
          sessionInfo.accountType,
          sessionInfo.userNameSei,
          sessionInfo.userNameMei,
          sessionInfo.permissions,
          sessionInfo.stationAreaDetectionFlag,
          sessionInfo.affiliations,
        );
      }
    }
    finally {
      endLoad();
    }
  };

  /**
   * 各Storeの内容を初期化する
   */
  const resetStore = () => {
    loginUserStore.$reset();
    useContainerInStore().$reset();
    useContainerOutStore().$reset();
    useMasterStore().reset();
  };

  /**
   * 代理ログインを終了する
   */
  const endProxyLogin = async () => {
    const proxyLoginMode = loginUserStore.loginUser?.proxyLoginMode;
    if (!proxyLoginMode) {
      return;
    }

    startLoad();
    try {
      const proxyRepository = new ProxyRepository(displayId);
      let response;
      if (proxyLoginMode === 'CH') {
        response = await proxyRepository.endChProxy();
      }
      else if (proxyLoginMode === 'MAX') {
        response = await proxyRepository.endMaxProxy();
      }
      else {
        logger.error(`不明な代理ログインモードです。proxyLoginMode: ${proxyLoginMode}`);
        throw new ClientError(MessageCodeEnum.UNKNOWN_ERROR, displayId);
      }

      if (!response.data) {
        logger.error('代理ログイン終了に失敗しました。');
        throw new ClientError(MessageCodeEnum.UNKNOWN_ERROR, displayId);
      }

      // ログインユーザの作業駅に組織情報を設定する
      const stationWithOrganization = response.data.workStation
        ? await getStationWithOrganizationByStationCd(
          response.data.workStation.stationCd,
          response.data.workStation.stationSubCd,
        )
        : undefined;

      // ストアの代理ログインユーザ情報をクリアし、元のユーザ情報に戻す
      loginUserStore.clearProxyLoginUser(
        response.data.platId,
        response.data.accountType,
        response.data.userNameSei,
        response.data.userNameMei,
        response.data.permissions,
        response.data.affiliations,
        stationWithOrganization,
      );
    }
    finally {
      endLoad();
    }
  };

  const _establishSession = async () => {
    // ログイン直後に要認証APIを1回呼び出してセッションを確立しておく
    await getSpeedMonitoringSettings();
  };

  return {
    loginWithQrCode,
    loginWithIdPassword,
    logout,
    checkAuth,
    refreshSessionInfo,
    resetStore,
    endProxyLogin,
  };
}
